import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  LinearProgress,
  Chip,
  Avatar,
  Divider
} from '@mui/material';
import {
  Receipt as ExpenseIcon,
  AccountBalance as BankIcon,
  CheckCircle as CheckIcon,
  Warning as WarningIcon,
  Add as AddIcon
} from '@mui/icons-material';

const MobileExpenseCard = ({ report, bankStatement }) => {
  
  const calculateProgress = () => {
    if (!report) return 0;
    
    const requiredReceipts = 5; // Assuming 5 receipts are typically required
    const uploadedReceipts = report.receipts ? report.receipts.length : 0;
    return Math.min((uploadedReceipts / requiredReceipts) * 100, 100);
  };

  const getStatusInfo = () => {
    if (!report) {
      return {
        status: 'not_started',
        statusText: 'Not started',
        color: 'text.disabled',
        chipColor: 'default',
        chipLabel: 'Pending'
      };
    }

    const progress = calculateProgress();
    const isComplete = report.status === 'completed';
    
    if (isComplete) {
      return {
        status: 'completed',
        statusText: 'Completed',
        color: 'success.main',
        chipColor: 'success',
        chipLabel: 'Complete'
      };
    } else if (progress >= 80) {
      return {
        status: 'almost_done',
        statusText: 'Almost done',
        color: 'warning.main',
        chipColor: 'warning',
        chipLabel: 'In Progress'
      };
    } else if (progress > 0) {
      return {
        status: 'in_progress',
        statusText: 'In progress',
        color: 'primary.main',
        chipColor: 'primary',
        chipLabel: 'In Progress'
      };
    } else {
      return {
        status: 'not_started',
        statusText: 'Not started',
        color: 'text.secondary',
        chipColor: 'default',
        chipLabel: 'Pending'
      };
    }
  };

  const statusInfo = getStatusInfo();
  const progress = calculateProgress();
  const currentMonth = new Date().toLocaleString('default', { month: 'long', year: 'numeric' });

  return (
    <Card className="mobile-card" sx={{ margin: '8px 16px' }}>
      <CardContent sx={{ pb: '16px !important' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Avatar
            sx={{
              bgcolor: statusInfo.color,
              width: 40,
              height: 40,
              mr: 2
            }}
          >
            <ExpenseIcon />
          </Avatar>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" className="mobile-subtitle" sx={{ mb: 0.5 }}>
              {currentMonth} Expense Report
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Monthly expense tracking
            </Typography>
          </Box>
          <Chip
            size="small"
            label={statusInfo.chipLabel}
            color={statusInfo.chipColor}
            variant="outlined"
          />
        </Box>

        {/* Progress Section */}
        {report && (
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Receipts Progress
              </Typography>
              <Typography variant="body2" color={statusInfo.color}>
                {report.receipts?.length || 0} receipts
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={progress}
              sx={{
                height: 6,
                borderRadius: 3,
                bgcolor: 'grey.200',
                '& .MuiLinearProgress-bar': {
                  bgcolor: statusInfo.color,
                  borderRadius: 3
                }
              }}
            />
          </Box>
        )}

        {/* Bank Statement Info */}
        {bankStatement && (
          <>
            <Divider sx={{ my: 2 }} />
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <BankIcon sx={{ color: 'primary.main', mr: 1 }} />
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2" fontWeight="medium">
                  Bank Statement Available
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {bankStatement.statement_date ? 
                    new Date(bankStatement.statement_date).toLocaleDateString() : 
                    'Date not available'
                  }
                </Typography>
              </Box>
              <CheckIcon sx={{ color: 'success.main' }} />
            </Box>
          </>
        )}

        {/* Action Buttons */}
        <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
          <Button
            component={RouterLink}
            to="/expense-report"
            variant="contained"
            size="small"
            startIcon={report ? <ExpenseIcon /> : <AddIcon />}
            sx={{ flex: 1, minHeight: 40 }}
          >
            {report ? 'View Report' : 'Start Report'}
          </Button>
          {report && (
            <Button
              variant="outlined"
              size="small"
              sx={{ minHeight: 40, minWidth: 80 }}
            >
              Upload
            </Button>
          )}
        </Box>

        {/* Summary Stats */}
        {report && report.receipts && report.receipts.length > 0 && (
          <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h6" color="primary">
                  ${report.total_amount?.toFixed(2) || '0.00'}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Total
                </Typography>
              </Box>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h6" color="success.main">
                  {report.receipts.filter(r => r.status === 'approved').length}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Approved
                </Typography>
              </Box>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h6" color="warning.main">
                  {report.receipts.filter(r => r.status === 'pending').length}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Pending
                </Typography>
              </Box>
            </Box>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default MobileExpenseCard; 